import { useMemo } from 'react'
import type { RefinementContext } from '@/features/_shared/channel-types'
import { findElementInTree } from '@/features/page-editor/store/canvas-slice'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { usePreviewSelectionStore } from '@/features/preview/store/preview-selection-slice'
import type { ChatContext } from '@/store/chat-types'
import type { ElementChip } from '@/store/ide-v2-slice'
import { useStudioStore } from '@/store/studio-store'
import type { RailItemId } from './types'

export type IdeSelectionSurface = 'preview' | 'page'

/**
 * The rail items on which an element/block/page selection is meaningful.
 *
 * Outside this set the visible surface has no element to point at, so a
 * selection carried over from a previous rail is stale rather than context:
 * `ConversationTargetSummary` refuses to name it, and
 * `activateStudioAuthorityTask` refuses to retain it. Both read this one
 * declaration — a second copy would let the two disagree silently.
 */
export const SELECTION_BEARING_RAIL_ITEMS: ReadonlySet<RailItemId> =
  new Set<RailItemId>(['preview', 'editor', 'behavior', 'data', 'library'])

export type IdeSelectionKind = 'preview-property' | 'element' | 'block' | 'page'

export interface IdeSelectionContext {
  surface: IdeSelectionSurface
  kind: IdeSelectionKind
  label: string
  detail: string
  pageId?: string
  pageName?: string
  blockName?: string
  blockId?: string
  blockType?: string
  elementId?: string
  elementType?: string
  propertyKey?: string
}

export function useStudioSelectionContext(): IdeSelectionContext | null {
  const activeRailItem = useStudioStore((s) => s.activeRailItem)
  const resourcesSelection = useStudioStore((s) => s.resourcesSelection)
  const previewSelection = usePreviewSelectionStore((s) => s.previewSelection)
  const selectedIds = useEditorStore((s) => s.selectedIds)
  const selectedBlockName = useEditorStore((s) => s.selectedBlockName)
  const selectedPageId = useEditorStore((s) => s.selectedPageId)
  const selectionTier = useEditorStore((s) => s.selectionTier)
  const blocks = useEditorStore((s) => s.blocks)
  const app = useEditorStore((s) => s.app)
  const activePageId = useEditorStore((s) => s.activePageId)

  return useMemo(() => {
    if (activeRailItem === 'library' && resourcesSelection) {
      return resourcesSelection
    }

    if (activeRailItem === 'preview' && previewSelection) {
      return {
        surface: 'preview',
        kind: 'preview-property',
        label: previewSelection.blockName,
        detail: `${previewSelection.blockType} / ${previewSelection.propertyKey}`,
        blockName: previewSelection.blockName,
        blockId: previewSelection.blockId,
        blockType: previewSelection.blockType,
        elementId: previewSelection.selectedElementId,
        propertyKey: previewSelection.propertyKey,
      }
    }

    const pageId = selectedPageId ?? activePageId ?? undefined
    const page = pageId ? app?.pages.find((candidate) => candidate.id === pageId) : undefined
    const pageName = page?.title ?? page?.id

    if (selectionTier === 'component' && selectedBlockName && selectedIds.length === 1) {
      const block = blocks[selectedBlockName]
      const element = block?.ui?.root
        ? findElementInTree(block.ui.root, selectedIds[0])
        : undefined
      return {
        surface: 'page',
        kind: 'element',
        label: selectedIds[0],
        detail: `${selectedBlockName}${element?.type ? ` / ${element.type}` : ''}`,
        pageId,
        pageName,
        blockName: selectedBlockName,
        blockType: block?.meta?.type,
        elementId: selectedIds[0],
        elementType: element?.type,
      }
    }

    if (selectionTier === 'block' && selectedBlockName) {
      const block = blocks[selectedBlockName]
      return {
        surface: 'page',
        kind: 'block',
        label: block?.meta?.name ?? selectedBlockName,
        detail: block?.meta?.type ?? 'block',
        pageId,
        pageName,
        blockName: selectedBlockName,
        blockType: block?.meta?.type,
      }
    }

    if (pageId) {
      return {
        surface: 'page',
        kind: 'page',
        label: pageName ?? pageId,
        detail: page?.route ?? pageId,
        pageId,
        pageName,
      }
    }

    if (previewSelection) {
      return {
        surface: 'preview',
        kind: 'preview-property',
        label: previewSelection.blockName,
        detail: `${previewSelection.blockType} / ${previewSelection.propertyKey}`,
        blockName: previewSelection.blockName,
        blockId: previewSelection.blockId,
        blockType: previewSelection.blockType,
        elementId: previewSelection.selectedElementId,
        propertyKey: previewSelection.propertyKey,
      }
    }

    return null
  }, [
    activeRailItem,
    activePageId,
    app,
    blocks,
    previewSelection,
    resourcesSelection,
    selectedBlockName,
    selectedIds,
    selectedPageId,
    selectionTier,
  ])
}

/**
 * Whether the page/block/element a selection points at still exists in the
 * live editor state.
 *
 * Single source of truth for "is this selection target still there?" — shared
 * by `isStudioAttentionTargetAvailable` (retained cockpit attention) and the
 * contextual task menu hook (current selection), which previously carried
 * byte-identical copies of this check.
 */
export function isEditorSelectionTargetAvailable(
  selection: IdeSelectionContext | null | undefined,
): boolean {
  if (!selection) return true
  const editor = useEditorStore.getState()
  if (selection.pageId && !editor.app?.pages.some((page) => page.id === selection.pageId)) {
    return false
  }
  if (!selection.blockName) return true
  const block = editor.blocks[selection.blockName]
  if (!block) return false
  return (
    selection.kind !== 'element' ||
    Boolean(selection.elementId && findElementInTree(block.ui.root, selection.elementId))
  )
}

export function getSelectionTitle(selection: IdeSelectionContext | null): string {
  if (!selection) return 'No selection'
  if (selection.kind === 'preview-property') return 'Target'
  if (selection.kind === 'element') return 'Selected element'
  if (selection.kind === 'block') return 'Selected block'
  return 'Current page'
}

export function buildSelectionPrompt(selection: IdeSelectionContext | null): string | null {
  if (!selection) return null
  const parts = [
    `surface=${selection.surface}`,
    `kind=${selection.kind}`,
    selection.pageId ? `page=${selection.pageId}` : null,
    selection.blockName ? `block=${selection.blockName}` : null,
    selection.blockType ? `blockType=${selection.blockType}` : null,
    selection.elementId ? `element=${selection.elementId}` : null,
    selection.elementType ? `elementType=${selection.elementType}` : null,
    selection.propertyKey ? `property=${selection.propertyKey}` : null,
  ].filter(Boolean)
  return parts.join('; ')
}

export function buildRefinementContext(
  selection: IdeSelectionContext | null,
): RefinementContext | undefined {
  if (!selection?.blockName) return undefined
  const elementId = selection.elementId ?? selection.propertyKey ?? selection.blockName
  return {
    element_id: elementId,
    element_type: selection.elementType ?? selection.blockType ?? selection.kind,
    current_state: {
      surface: selection.surface,
      kind: selection.kind,
      propertyKey: selection.propertyKey,
      pageId: selection.pageId,
    },
    block_id: selection.blockName,
  }
}

export function buildSelectionChip(selection: IdeSelectionContext | null): ElementChip | null {
  if (!selection) return null
  const blockId = selection.blockName ?? selection.blockId ?? selection.pageId ?? selection.kind
  const elementId = selection.elementId ?? selection.propertyKey ?? selection.pageId ?? blockId
  const elementType = selection.elementType ?? selection.blockType ?? selection.kind
  return {
    element_id: elementId,
    element_type: elementType,
    block_id: blockId,
    label: selection.label,
  }
}

export function buildSelectionChatContext(
  selection: IdeSelectionContext | null,
  current: ChatContext,
): ChatContext {
  const base: ChatContext = {
    ...current,
    pageId: undefined,
    pageName: undefined,
    flowId: undefined,
    flowName: undefined,
    selectionSurface: undefined,
    selectedElementId: undefined,
    selectedElementLabel: undefined,
    selectedBlockName: undefined,
    selectedBlockLabel: undefined,
    selectedBlockType: undefined,
    selectedElementType: undefined,
    selectedPropertyKey: undefined,
  }
  if (!selection) return base

  return {
    ...base,
    pageId: selection.pageId,
    pageName: selection.pageName ?? (selection.kind === 'page' ? selection.label : undefined),
    selectionSurface: selection.surface,
    selectedElementId: selection.elementId,
    selectedElementLabel:
      selection.kind === 'element' ? selection.label : undefined,
    selectedBlockName: selection.blockName,
    selectedBlockLabel:
      selection.kind === 'block' ? selection.label : undefined,
    selectedBlockType: selection.blockType,
    selectedElementType: selection.elementType,
    selectedPropertyKey: selection.propertyKey,
  }
}

export function getSelectionSearchTerms(selection: IdeSelectionContext | null): string[] {
  if (!selection) return []
  return [
    selection.blockName,
    selection.blockType,
    selection.elementType,
    selection.propertyKey,
    selection.label,
  ]
    .filter((term): term is string => Boolean(term))
    .flatMap((term) => term.toLowerCase().split(/[^a-z0-9]+/))
    .filter((term) => term.length > 1)
}
